import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import SendIcon from '@mui/icons-material/Send';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Drawer,
  LinearProgress,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import EmployeeAvatar from 'components/@home/EmployeeAvatar';
import CloseButton from 'components/@home/drawers/CloseButton';
import { bool, func, string } from 'prop-types';
import React, { useMemo } from 'react';
import { useListBulkMessagingRecipientsQuery } from 'store/app/entities/SharedFilesSlice';
import { I18n } from 'utils/i18n';

/** Same pattern as EmployeeListDrawer/MemberList/MemberListItem StatusBadge */
const RecipientStatusLine = ({ label, paletteColor, Icon }) => (
  <Box
    sx={[
      {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '3px',
      },
      theme => ({
        color: (theme.palette[paletteColor] || theme.palette.warning).main,
      }),
    ]}
  >
    <Typography
      component="span"
      noWrap
      sx={{
        fontSize: '0.6875rem',
        fontWeight: 500,
        lineHeight: 1,
        textAlign: 'right',
      }}
    >
      {label}
    </Typography>
    <Icon sx={{ fontSize: 14, flexShrink: 0 }} />
  </Box>
);

const getSignerChipColor = signerStatus => {
  switch (signerStatus) {
    case 'signed':
      return 'success';
    case 'declined':
    case 'expired':
      return 'error';
    case 'viewed':
    case 'sent':
      return 'info';
    case 'pending':
    default:
      return 'warning';
  }
};

const getBulkChipColor = status => {
  switch (status) {
    case 'FINISHED':
      return 'success';
    case 'ABORTED':
      return 'error';
    case 'IN_PROGRESS':
      return 'info';
    case 'PENDING':
    default:
      return 'warning';
  }
};

const getRecipientStatusLabel = r => {
  if (r.signerStatus != null || r.channel === 'email') {
    return I18n.t(`SignTracking.recipientsDrawer.signerStatus.${r.signerStatus || 'pending'}`);
  }
  return I18n.t(`BulkMessaging.Status.${r.status}`);
};

const getRecipientStatusColor = r => {
  if (r.signerStatus != null || r.channel === 'email') {
    return getSignerChipColor(r.signerStatus || 'pending');
  }
  return getBulkChipColor(r.status);
};

/** Same line as EmployeeListDrawer/MemberList/MemberListItem jobLine */
const getRecipientJobLine = r => {
  const titleDept = [r.jobTitle, r.department].filter(Boolean).join(', ');
  if (titleDept) return titleDept;
  return [r.division, r.code].filter(Boolean).join(' · ');
};

const getRecipientStatusIcon = r => {
  if (r.signerStatus != null || r.channel === 'email') {
    switch (r.signerStatus) {
      case 'signed':
        return CheckCircleIcon;
      case 'declined':
        return CancelIcon;
      case 'expired':
        return EventBusyIcon;
      case 'viewed':
        return VisibilityIcon;
      case 'sent':
        return SendIcon;
      case 'pending':
      default:
        return HourglassEmptyIcon;
    }
  }
  switch (r.status) {
    case 'FINISHED':
      return CheckCircleIcon;
    case 'ABORTED':
      return CancelIcon;
    case 'IN_PROGRESS':
      return SyncIcon;
    case 'PENDING':
    default:
      return HourglassEmptyIcon;
  }
};

const SignTrackingRecipientsDrawer = ({
  open,
  onClose,
  formatId = null,
  bulkMessagingId = null,
  templateName = '',
  onDownloadZip = null,
  downloadBusy = false,
}) => {
  const { data, isFetching } = useListBulkMessagingRecipientsQuery(
    { formatId, bulkMessagingId, page: 1, perPage: 100 },
    { skip: !open || !formatId || !bulkMessagingId },
  );

  const recipients = data?.recipients || [];
  const externalOnlyCount = data?.externalOnlyCount ?? 0;
  const summary = data?.bulkMessagingSummary;

  const signMode = summary?.signProgressEligible === true;
  const progress = useMemo(() => {
    const total = summary?.total ?? 0;
    if (!total) return 0;
    const done = signMode ? (summary?.signedCount ?? 0) : (summary?.sent ?? 0);
    return Math.min(100, Math.round((done / total) * 100));
  }, [summary, signMode]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: '100%' }}>
        <Box sx={{ pt: 2.5, pl: 2.5, flexShrink: 0 }}>
          <CloseButton onClick={onClose} />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: { xs: 0, sm: 500 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            pt: 2.5,
            px: 3.75,
            pb: 2,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2.5,
              }}
            >
              <Typography variant="h5" component="h2">
                {I18n.t('SignTracking.recipientsDrawer.title')}
              </Typography>
            </Box>

            {templateName ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  p: 2,
                  mb: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.contrastText',
                    flexShrink: 0,
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      maxWidth: 350,
                    }}
                  >
                    {templateName}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {externalOnlyCount > 0 ? (
              <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
                {I18n.t('SignTracking.recipientsDrawer.externalOnly', { count: externalOnlyCount })}
              </Alert>
            ) : null}
            {data?.internalTruncated ? (
              <Alert severity="warning" sx={{ mb: 2 }} variant="outlined">
                {I18n.t('SignTracking.recipientsDrawer.listTruncated', { max: 3000 })}
              </Alert>
            ) : null}

            {summary ? (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    {signMode
                      ? I18n.t('SignTracking.recipientsDrawer.signProgressHint', {
                          signed: summary.signedCount ?? 0,
                          total: summary.total ?? 0,
                        })
                      : I18n.t('SignTracking.recipientsDrawer.progressHint', {
                          sent: summary.sent ?? 0,
                          total: summary.total ?? 0,
                        })}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                      '& .MuiLinearProgress-bar': { borderRadius: 3 },
                    }}
                  />
                </CardContent>
              </Card>
            ) : null}

            {onDownloadZip ? (
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<DownloadOutlinedIcon />}
                disabled={downloadBusy || !formatId || !bulkMessagingId}
                onClick={e => {
                  e.preventDefault();
                  onDownloadZip(e);
                }}
                sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
              >
                {I18n.t('SignTracking.downloadBulk')}
              </Button>
            ) : null}

            {isFetching && !recipients.length ? (
              <Stack
                spacing={1.5}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 6,
                }}
              >
                <CircularProgress size={32} />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {I18n.t('SignTracking.recipientsDrawer.loading')}
                </Typography>
              </Stack>
            ) : (
              <List disablePadding>
                {recipients.map(r => {
                  const statusColor = getRecipientStatusColor(r);
                  const statusLabel = getRecipientStatusLabel(r);
                  const StatusIcon = getRecipientStatusIcon(r);
                  const isEmail = r.channel === 'email';
                  const jobLine = getRecipientJobLine(r);
                  const hasEmployeeAvatar = Boolean(r.employeePreview?._id);
                  const avatarEmployee = hasEmployeeAvatar
                    ? {
                        _id: r.employeePreview._id,
                        name: r.employeePreview.name || r.displayName || '',
                        avatar: r.employeePreview.avatar || '',
                      }
                    : null;
                  const avatarStatus = r.isAcceptTerms === false ? 'pending' : 'active';

                  return (
                    <ListItem
                      key={`${r.channel || 'internal'}-${r._id}`}
                      disableGutters
                      sx={{
                        boxSizing: 'border-box',
                        alignItems: 'stretch',
                      }}
                    >
                      <Box
                        sx={{
                          mr: 1.5,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          alignSelf: 'center',
                        }}
                      >
                        {avatarEmployee ? (
                          <EmployeeAvatar large employee={avatarEmployee} status={avatarStatus} />
                        ) : (
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: '50%',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: theme => alpha(theme.palette.text.primary, 0.06),
                              color: 'text.secondary',
                            }}
                          >
                            {isEmail ? (
                              <MailOutlineIcon sx={{ fontSize: 22 }} />
                            ) : (
                              <PersonOutlineIcon sx={{ fontSize: 22 }} />
                            )}
                          </Box>
                        )}
                      </Box>
                      <Stack
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          borderBottom: 1,
                          borderColor: 'divider',
                          py: 1.25,
                          pr: 1,
                        }}
                      >
                        <Box sx={{ flexShrink: 0, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'baseline',
                              justifyContent: 'space-between',
                              gap: 1,
                            }}
                          >
                            <Typography
                              noWrap
                              title={r.displayName}
                              sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                flex: 1,
                                minWidth: 0,
                                color: r.isAcceptTerms === false ? 'secondary.main' : 'inherit',
                              }}
                            >
                              {r.displayName}
                            </Typography>
                          </Box>
                          {jobLine ? (
                            <Typography
                              component="div"
                              variant="caption"
                              noWrap
                              title={jobLine}
                              sx={{
                                color: 'text.secondary',
                                mt: 0.25,
                                lineHeight: 1.43,
                                mb: r.email ? 0.25 : isEmail ? 0.25 : 0.75,
                              }}
                            >
                              {jobLine}
                            </Typography>
                          ) : null}
                          {r.email ? (
                            <Typography
                              component="div"
                              variant="caption"
                              noWrap
                              title={r.email}
                              sx={{
                                color: 'text.secondary',
                                mt: jobLine ? 0 : 0.25,
                                lineHeight: 1.43,
                                mb: isEmail ? 0.25 : 0.75,
                              }}
                            >
                              {r.email}
                            </Typography>
                          ) : null}
                          {isEmail ? (
                            <Typography
                              component="div"
                              variant="caption"
                              sx={{
                                color: 'info.dark',
                                mt: 0.25,
                                mb: 0.75,
                                lineHeight: 1.43,
                                fontWeight: 500,
                              }}
                            >
                              {I18n.t('SignTracking.recipientsDrawer.channelBadge')}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                      <Stack
                        direction="column"
                        sx={{
                          borderBottom: 1,
                          borderColor: 'divider',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          py: 1.25,
                          pl: 1,
                          flexShrink: 0,
                        }}
                      >
                        <RecipientStatusLine
                          label={statusLabel}
                          paletteColor={statusColor}
                          Icon={StatusIcon}
                        />
                      </Stack>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {!isFetching && !recipients.length && !externalOnlyCount ? (
              <Box
                sx={{
                  mt: 2,
                  py: 4,
                  px: 2,
                  textAlign: 'center',
                  borderRadius: 2,
                  border: 1,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {I18n.t('SignTracking.recipientsDrawer.emptyInternal')}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

SignTrackingRecipientsDrawer.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  formatId: string,
  bulkMessagingId: string,
  templateName: string,
  onDownloadZip: func,
  downloadBusy: bool,
};

export default SignTrackingRecipientsDrawer;
